import {
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  deleteUser as deleteAuthUser,
  onAuthStateChanged,
  reauthenticateWithCredential,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  verifyBeforeUpdateEmail,
  type Unsubscribe,
  type User,
} from 'firebase/auth'
import { auth } from '@core/services/firebase/firebase-service'
import { AuthErrorConverter } from '@core/services/authentication/converters/auth-error-converter'
import { AuthUserConverter } from '@core/services/authentication/converters/auth-user-converter'
import { AuthUser } from '@domain/models/auth-user'
import { CoreError, CoreErrorCause } from '@domain/models/core-error'

function normalizeEmail(email: string) {
  return email.trim().toLowerCase()
}

function getAuthInstance() {
  if (!auth) {
    throw new CoreError({
      cause: CoreErrorCause.Unknown,
      context: 'Firebase Auth is not initialized.',
    })
  }
  return auth
}

export class AuthenticationService {
  private static instance: AuthenticationService

  private constructor() {}

  public static getInstance(): AuthenticationService {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = new AuthenticationService()
    }
    return AuthenticationService.instance
  }

  public get userId(): string | null {
    return auth?.currentUser?.uid ?? null
  }

  public get isUserLoggedIn(): boolean {
    return Boolean(auth?.currentUser)
  }

  public get authUser(): AuthUser | null {
    return AuthUserConverter.domainModel(auth?.currentUser ?? null)
  }

  public observeAuthState(callback: (user: User | null) => void): Unsubscribe {
    if (!auth) {
      callback(null)
      return () => {}
    }

    return onAuthStateChanged(auth, callback)
  }

  public async login(email: string, password: string): Promise<User> {
    try {
      const credential = await signInWithEmailAndPassword(
        getAuthInstance(),
        normalizeEmail(email),
        password,
      )
      return credential.user
    } catch (error) {
      throw AuthErrorConverter.domainModel(error)
    }
  }

  public async signIn(email: string, password: string): Promise<User> {
    return this.login(email, password)
  }

  public async signUp(email: string, password: string): Promise<User> {
    try {
      const credential = await createUserWithEmailAndPassword(
        getAuthInstance(),
        normalizeEmail(email),
        password,
      )
      return credential.user
    } catch (error) {
      throw AuthErrorConverter.domainModel(error)
    }
  }

  public async resetPassword(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(getAuthInstance(), normalizeEmail(email))
    } catch (error) {
      throw AuthErrorConverter.domainModel(error)
    }
  }

  public async logout(): Promise<void> {
    try {
      await signOut(getAuthInstance())
    } catch (error) {
      throw AuthErrorConverter.domainModel(error)
    }
  }

  public async signOut(): Promise<void> {
    return this.logout()
  }

  public async reauthenticateUser(currentPassword: string): Promise<void> {
    try {
      const user = getAuthInstance().currentUser
      if (!user || !user.email) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: 'No signed-in user with email found.',
        })
      }

      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)
    } catch (error) {
      throw AuthErrorConverter.domainModel(error)
    }
  }

  public async verifyAndUpdateEmail(
    newEmail: string,
    currentPassword: string,
  ): Promise<void> {
    try {
      const user = getAuthInstance().currentUser
      if (!user || !user.email) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: 'No signed-in user with email found.',
        })
      }

      const credential = EmailAuthProvider.credential(user.email, currentPassword)
      await reauthenticateWithCredential(user, credential)
      await verifyBeforeUpdateEmail(user, normalizeEmail(newEmail))
    } catch (error) {
      throw AuthErrorConverter.domainModel(error)
    }
  }

  public async deleteUser(): Promise<void> {
    try {
      const user = getAuthInstance().currentUser
      if (!user) {
        throw new CoreError({
          cause: CoreErrorCause.DataNotFound,
          context: 'No signed-in user found to delete.',
        })
      }

      await deleteAuthUser(user)
    } catch (error) {
      throw AuthErrorConverter.domainModel(error)
    }
  }

  public async deleteAccount(): Promise<void> {
    await this.deleteUser()
  }
}

